import LegalPage from "../LegalPage";
import { PIGGY_EMAIL } from "../constants";

const Mail = ({ subject }) => (
  <a href={`mailto:${PIGGY_EMAIL}${subject ? `?subject=${encodeURIComponent(subject)}` : ""}`}>
    {PIGGY_EMAIL}
  </a>
);

const SECTIONS = [
  {
    id: "who",
    title: "Who we are and what this covers",
    body: (
      <>
        <p>
          Piggy is an independent personal-finance app built and operated by
          its developer ("we", "us"), based in India. It is not a bank, a
          payment provider, or an account aggregator. This policy covers the Piggy Android app, the Piggy web
          dashboard, the Piggy API that both talk to, and this website
          (together, the "Service").
        </p>
        <p>
          For anything in this policy, write to <Mail subject="Piggy privacy" />.
          We are the data fiduciary / controller for the personal data described
          here.
        </p>
      </>
    ),
  },
  {
    id: "summary",
    title: "The short version",
    body: (
      <>
        <ul>
          <li>
            Piggy reads <strong>bank, card, UPI, and merchant transaction
            alerts</strong> from your SMS inbox and, if you choose, your Gmail.
            It turns each alert into one ledger row: amount, payee, time,
            category.
          </li>
          <li>
            It <strong>does not</strong> read OTPs, verification codes,
            personal conversations, or mail from senders you have not allowed.
            Filtering happens on your phone before anything is uploaded.
          </li>
          <li>
            Gmail access is <strong>optional and read-only</strong>. Piggy never
            sends, deletes, labels, or modifies email.
          </li>
          <li>
            Alert text is sent to an AI model provider to extract the
            transaction. Only the alert text goes; nothing about you or your
            account is attached.
          </li>
          <li>
            We <strong>do not sell</strong> your data, show ads, or share your
            ledger with anyone. There is no third-party analytics or tracking
            in the app.
          </li>
          <li>
            You can delete transactions in the app at any time, disconnect
            Gmail in Settings, and ask us to delete your whole account.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: "collect",
    title: "What we collect",
    body: (
      <>
        <h3>Account</h3>
        <p>
          You sign in with Google. We receive your <strong>Google account email
          address</strong> and a verified sign-in token. We never see your
          Google password. Your email is your identity in Piggy: it is how your
          ledger is kept separate from everyone else's.
        </p>

        <h3>SMS transaction alerts (Android)</h3>
        <p>
          With your permission (<code>RECEIVE_SMS</code> / <code>READ_SMS</code>
          ), the app watches incoming SMS. A filter <strong>on your device</strong>{" "}
          keeps only messages that (a) come from a bank, card, UPI, or merchant
          sender you have allowed and (b) look like a transaction alert. OTPs,
          verification codes, statement notices, failed or declined payments,
          balance-due reminders, and every message from any other sender are
          dropped on the phone and never leave it.
        </p>
        <p>
          For messages that pass the filter, the app uploads the{" "}
          <strong>sender ID, message text, and received time</strong> to the
          Piggy API so the transaction can be extracted. That raw message is
          kept in a processing inbox and is <strong>automatically deleted 14
          days after it has been processed</strong>. Only the extracted
          transaction (see below) is kept long-term.
        </p>

        <h3>Gmail transaction alerts (optional)</h3>
        <p>
          If you connect Gmail, you grant Piggy the{" "}
          <code>gmail.readonly</code> scope through Google's consent screen.
          We store an OAuth refresh token for your mailbox on our server so
          Piggy can keep checking for new alerts while the app is closed. Piggy
          looks only for messages from senders on your allow-list (common Indian
          banks and card issuers are pre-filled; you can add or remove any). It
          reads the matching messages to extract the transaction. It skips
          monthly statements, OTPs, and promotional mail.
        </p>
        <p>
          We do <strong>not</strong> store the full content of your emails. For
          each transaction we keep the sender address, the subject line, and the
          Gmail message ID so that the same alert is never counted twice. For a
          message you explicitly pick in "Browse mail", we also keep Gmail's
          short snippet until it is processed.
        </p>
        <p className="piggy-legal__callout">
          Piggy's use and transfer of information received from Google APIs to
          any other app adheres to the{" "}
          <a
            href="https://developers.google.com/terms/api-services-user-data-policy#additional_requirements_for_specific_api_scopes"
            target="_blank"
            rel="noopener noreferrer"
          >
            Google API Services User Data Policy
          </a>
          , including the Limited Use requirements. Gmail data is used only to
          provide the ledger feature you see in the app. It is never used for
          advertising, never sold, and never read by a human except with your
          explicit permission for support or as required for security or legal
          reasons.
        </p>

        <h3>The ledger we build</h3>
        <p>
          From each alert, Piggy stores a transaction record: amount, currency,
          debit or credit, date and time, payee or merchant, category, a short
          description, a confidence score, the source (SMS, email, manual, or
          statement), a bank reference number when present, a masked account
          hint (for example the last digits your bank prints), and the
          available balance if the alert included one. Full card numbers or
          full account numbers are not extracted or stored.
        </p>

        <h3>Things you type or import</h3>
        <ul>
          <li>Manual transactions and edits to any transaction.</li>
          <li>
            Bank or card statement CSVs you import to fill gaps. The file text
            is kept until it has been processed into transactions.
          </li>
          <li>
            Categories, merchant rules (so a payee is categorised the same way
            next time), SMS and email allow-lists, and display preferences.
          </li>
        </ul>

        <h3>Technical data</h3>
        <p>
          The API logs request paths, timestamps, and status codes for
          reliability. It does not log message bodies. The Android app does
          not include any third-party analytics, crash reporting, or
          advertising SDK. This website uses Google Analytics to
          count visits; see "Cookies" below.
        </p>
      </>
    ),
  },
  {
    id: "permissions",
    title: "Android permissions, and why",
    body: (
      <table>
        <thead>
          <tr>
            <th>Permission</th>
            <th>Why Piggy asks</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Receive / read SMS</td>
            <td>
              To notice bank and UPI alerts as they arrive. Filtering happens on
              device. Required for SMS sync; the app works without it if you
              use Gmail or manual entry.
            </td>
          </tr>
          <tr>
            <td>Notifications</td>
            <td>
              Optional. For a daily summary and to tell you when something needs
              Review.
            </td>
          </tr>
          <tr>
            <td>Ignore battery optimisation</td>
            <td>
              Optional. Lets the SMS listener keep running on phones that
              aggressively kill background apps.
            </td>
          </tr>
          <tr>
            <td>Internet</td>
            <td>To talk to the Piggy API.</td>
          </tr>
          <tr>
            <td>Boot completed, wake lock, foreground service</td>
            <td>
              To resume listening for SMS after a restart and finish uploading a
              queued alert.
            </td>
          </tr>
          <tr>
            <td>Device lock / biometrics</td>
            <td>
              To lock the app behind your fingerprint or PIN. Android handles
              the check; Piggy never receives biometric data.
            </td>
          </tr>
        </tbody>
      </table>
    ),
  },
  {
    id: "ai",
    title: "How AI is used",
    body: (
      <>
        <p>
          Piggy uses a large language model to read an alert and pull out the
          amount, payee, time, type, and a category. To do that, the{" "}
          <strong>text of the alert</strong> (the SMS body, or the relevant
          part of the email) is sent from our server to an AI model provider's
          API. Depending on configuration this is one of OpenAI, Anthropic,
          Google (Gemini), or Cursor.
        </p>
        <ul>
          <li>
            Only the alert text is sent. Your name, email address, and account
            identity are not included in the request.
          </li>
          <li>
            We use these providers through their developer APIs, not their
            consumer chat products, and we choose plans under which API inputs
            are not used to train their models. Their own short retention
            windows for abuse monitoring apply.
          </li>
          <li>
            The model's output is a structured transaction, which is what we
            store. When the model is not confident, the item goes to Review for
            you to decide, rather than being guessed.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: "use",
    title: "How we use your data",
    body: (
      <>
        <p>We use the data above only to:</p>
        <ul>
          <li>Show you your ledger, summaries, and analytics.</li>
          <li>
            Detect duplicates (the same payment reported by both SMS and email)
            and link refunds to the original payment.
          </li>
          <li>Apply the category rules you have taught it.</li>
          <li>Keep the Service running, secure, and free of abuse.</li>
          <li>Answer you when you write to us.</li>
        </ul>
        <p>
          We do not use your data for advertising, profiling for marketing,
          credit decisions, or to build products for anyone else. We do not
          sell or rent personal data, and we have never done so.
        </p>
      </>
    ),
  },
  {
    id: "sharing",
    title: "Who else sees it",
    body: (
      <>
        <p>
          Nobody sees your ledger but you. To run the Service we rely on a few
          processors who handle data on our instructions:
        </p>
        <table>
          <thead>
            <tr>
              <th>Provider</th>
              <th>What they handle</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Amazon Web Services (Mumbai region)</td>
              <td>Hosts the Piggy API and its Postgres database.</td>
            </tr>
            <tr>
              <td>Google</td>
              <td>
                Sign-in with Google; Gmail API when you connect Gmail. Governed
                by Google's own privacy policy.
              </td>
            </tr>
            <tr>
              <td>AI model provider (one of OpenAI, Anthropic, Google, Cursor)</td>
              <td>Receives alert text to extract the transaction. See "How AI is used".</td>
            </tr>
          </tbody>
        </table>
        <p>
          Beyond that, we would disclose data only if required by law or to
          protect the rights and safety of users or the Service, and if Piggy
          were ever transferred to a new owner, in which case this policy would
          continue to apply and you would be told first.
        </p>
      </>
    ),
  },
  {
    id: "retention",
    title: "How long we keep it",
    body: (
      <table>
        <thead>
          <tr>
            <th>Data</th>
            <th>Kept until</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Raw SMS in the processing inbox</td>
            <td>Deleted automatically 14 days after processing.</td>
          </tr>
          <tr>
            <td>Statement CSV text</td>
            <td>Until processed into transactions, then removed.</td>
          </tr>
          <tr>
            <td>Transactions, categories, rules, allow-lists</td>
            <td>Until you delete them in the app or delete your account.</td>
          </tr>
          <tr>
            <td>Gmail refresh token</td>
            <td>
              Until you disconnect Gmail in Settings, revoke Piggy at{" "}
              <a href="https://myaccount.google.com/permissions" target="_blank" rel="noopener noreferrer">
                myaccount.google.com/permissions
              </a>
              , or delete your account. It is then discarded immediately.
            </td>
          </tr>
          <tr>
            <td>Account (email, session)</td>
            <td>Until you ask us to delete it. Deletion cascades to everything above.</td>
          </tr>
          <tr>
            <td>Server request logs</td>
            <td>Kept briefly for troubleshooting and rotated; they contain no message text.</td>
          </tr>
        </tbody>
      </table>
    ),
  },
  {
    id: "control",
    title: "Your choices and rights",
    body: (
      <>
        <ul>
          <li>
            <strong>Edit or delete</strong> any transaction, category, rule, or
            allow-list entry directly in the app or dashboard.
          </li>
          <li>
            <strong>Turn SMS or email sync off</strong> in Settings at any time.
            Revoking the SMS permission in Android settings has the same effect.
          </li>
          <li>
            <strong>Disconnect Gmail</strong> in Settings, or revoke Piggy's
            access from your Google Account. Either way the refresh token is
            discarded.
          </li>
          <li>
            <strong>Delete your account</strong> by emailing{" "}
            <Mail subject="Delete my Piggy account" /> from the Google address
            you signed in with. We delete the account and all associated data
            within 30 days and confirm by reply. In-app account deletion is
            coming with the first public release.
          </li>
          <li>
            <strong>Access or export</strong>: ask and we will send you a copy
            of your data in CSV or JSON.
          </li>
        </ul>
        <p>
          Under India's Digital Personal Data Protection Act, 2023 you have the
          right to access, correct, and erase your personal data, to nominate
          someone to exercise these rights on your behalf, and to raise a
          grievance. If you are in the EU or UK, the GDPR gives you similar
          rights plus portability and objection; our lawful basis is performance
          of the Service you asked for (Art. 6(1)(b)) and, for Gmail access,
          your consent (Art. 6(1)(a)). Contact us first; if you are not
          satisfied you may approach the Data Protection Board of India or your
          local supervisory authority.
        </p>
      </>
    ),
  },
  {
    id: "security",
    title: "Security",
    body: (
      <>
        <p>
          Piggy is small, and we keep the attack surface small too. Your
          session token is stored hashed on the server, so a database leak
          would not reveal working credentials. Your Gmail refresh token lives
          only on the server and is never sent to the phone or browser. Server
          access is limited to the developer, over key-based SSH from a fixed
          address. The Android app can be locked behind your device biometrics.
        </p>
        <p>
          No system is perfectly secure. If we learn of a breach affecting your
          data, we will tell you by email without undue delay and report it to
          the authorities where the law requires.
        </p>
      </>
    ),
  },
  {
    id: "cookies",
    title: "Cookies and local storage",
    body: (
      <>
        <p>
          The Piggy app and web dashboard use local storage only to keep you
          signed in and remember your display preferences. They set no
          third-party cookies.
        </p>
        <p>
          This website uses Google Analytics, which sets cookies
          to count visits and pages viewed. You can block it with a browser
          setting or extension without affecting Piggy.
        </p>
      </>
    ),
  },
  {
    id: "children",
    title: "Children",
    body: (
      <p>
        Piggy is a financial tool and is intended for adults. It is not
        directed at anyone under 18, and we do not knowingly collect data from
        children. If you believe a child has created an account, email us and
        we will delete it.
      </p>
    ),
  },
  {
    id: "changes",
    title: "Changes to this policy",
    body: (
      <p>
        When we change this policy we will update the date at the top and, for
        anything material (for example, a new category of data or a new
        processor), notify you in the app before it takes effect. Earlier
        versions are available on request.
      </p>
    ),
  },
  {
    id: "contact",
    title: "Contact",
    body: (
      <p>
        Piggy · <Mail subject="Piggy privacy" />. We aim to reply within seven
        days.
      </p>
    ),
  },
];

const Privacy = () => (
  <LegalPage
    kind="privacy"
    title="Privacy policy"
    description="How Piggy handles your SMS and Gmail bank alerts, what it stores, and how to delete it."
    intro={
      <>
        <p>
          Piggy exists to read the transaction alerts your bank already sends
          you and turn them into a ledger. That means it touches sensitive
          things: your messages and your spending. This page explains, plainly,
          what Piggy reads, what it keeps, who else is involved, and how to make
          it stop.
        </p>
        <p>
          <strong>If you only read one line:</strong> Piggy reads bank alerts
          and nothing else, keeps only the transactions it extracts, never sells
          or shares your ledger, and deletes everything when you ask.
        </p>
      </>
    }
    sections={SECTIONS}
  />
);

export default Privacy;
